import InputTextField from '../../../../../components/InputTextField/InputTextField'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, CircularProgress, Stack, useTheme } from '@mui/material'
import { Trans } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../../../store/utils/hooks'
import {
	actionCreateCrmContact,
	actionCreateStripeCheckoutSession,
	actionSaveOnboardingData,
	selectOnboardingData,
	selectStripeCustomerData,
} from '../../../../../store/slices/user/userSlice'
import { validateEmailAddress, validatePhoneNumber } from '../../../../../utils/Validators/Validators'
import PhoneNumberMask from '../../../../../components/PhoneNumberMask/PhoneNumberMask'
import useCheckEmail from '../../../hooks/useCheckEmail'
import { signInAnonymousUser, userSignOut } from '../../../../../utils/Firebase/methods'
import { useNavigate } from 'react-router-dom'
import { LOGIN_PATH } from '../../../../../routes/LoginRoutes'
import urlBuilder from '../../../../../utils/UrlBuilder'
import { ONBOARDING_PATH, PAYMENT_PATH } from '../../../../../routes/OnboardingRoutes'

type RegisterFormProps = {
	prefillData?: { email: string; phoneNumber: string }
}

export default function RegisterForm({ prefillData }: RegisterFormProps) {
	const { palette } = useTheme()
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const onboardingData = useAppSelector(selectOnboardingData)
	const { data: stripeCustomerData } = useAppSelector(selectStripeCustomerData)
	const [canMoveForward, setCanMoveForward] = useState<boolean>(false)
	const { loading } = useCheckEmail()
	const [loader, setLoader] = useState<boolean>(false)

	const setEmail = useCallback((email: string) => dispatch(actionSaveOnboardingData({ email })), [dispatch])
	const setPhoneNumber = useCallback(
		(phoneNumber: string) => dispatch(actionSaveOnboardingData({ phoneNumber })),
		[dispatch],
	)

	useEffect(() => {
		let emptyPhoneNumber = true
		let emptyEmailAddress = true
		if (prefillData?.phoneNumber) {
			setEmail(prefillData.email)
			const phoneNumber =
				prefillData.phoneNumber.length > 10 ? prefillData.phoneNumber.slice(-10) : prefillData.phoneNumber
			console.log('phoneNumber', phoneNumber)
			const phoneNumberWithParentheses = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`

			emptyPhoneNumber = !validatePhoneNumber(phoneNumberWithParentheses)
			setPhoneNumber(phoneNumberWithParentheses)
		}
		// fill in email data if available
		if (prefillData?.email) {
			emptyEmailAddress = !validateEmailAddress(prefillData.email)
			setEmail(prefillData.email)
		}

		setCanMoveForward(!emptyEmailAddress && !emptyPhoneNumber && !loading)
	}, [dispatch, prefillData, loading, setEmail, setPhoneNumber])

	useEffect(() => {
		const emptyEmailAddress = !validateEmailAddress(onboardingData.email)
		const emptyPhoneNumber = !validatePhoneNumber(onboardingData.phoneNumber)
		setCanMoveForward(!emptyEmailAddress && !emptyPhoneNumber && !loading)
	}, [onboardingData.email, onboardingData.phoneNumber, loading])

	const queryParams = useMemo(() => new URLSearchParams(window.location.search), [])

	const handleNext = useCallback(async () => {
		try {
			setLoader(true)
			const { email, phoneNumber } = onboardingData
			await signInAnonymousUser()
			const utm = {
				utm_source: queryParams.get('utm_source') || '',
				utm_medium: queryParams.get('utm_medium') || '',
				utm_term: queryParams.get('utm_term') || '',
				utm_content: queryParams.get('utm_content') || '',
			}
			dispatch(actionCreateCrmContact({ email, phoneNumber, utm }))
			if (!stripeCustomerData.client_secret) {
				dispatch(actionCreateStripeCheckoutSession({ email, phoneNumber }))
			}
			await userSignOut()
			navigate(urlBuilder([ONBOARDING_PATH, PAYMENT_PATH]))
		} catch (e) {
			setLoader(false)
		}
	}, [dispatch, navigate, onboardingData, queryParams])

	return (
		<Stack gap="16px" width="100%" maxWidth="360px">
			<InputTextField
				value={onboardingData?.email}
				type="email"
				onChange={(e) => setEmail(e.target.value)}
				i18nLabel="onboarding.register.email_input_label"
				i18nPlaceholder="onboarding.register.email_input_placeholder"
				name="email"
				required
			/>
			<InputTextField
				value={onboardingData?.phoneNumber}
				onChange={(e) => setPhoneNumber(e.target.value)}
				i18nLabel="onboarding.register.phone_number_input_label"
				i18nPlaceholder="onboarding.register.phone_number_input_placeholder"
				name="phoneNumber"
				required
				inputComponent={PhoneNumberMask}
			/>
			<Button
				variant="contained"
				sx={{ backgroundColor: palette.brand.main, height: '48px' }}
				disabled={!canMoveForward || loader}
				onClick={handleNext}
				fullWidth
			>
				{loader ? (
					<CircularProgress style={{ height: '25px', width: '25px' }} />
				) : (
					<Trans i18nKey="onboarding.register.join_now_button_label" />
				)}
			</Button>
		</Stack>
	)
}
